// Behind the answer (TOM-710, the Marina chat spec): what one answer rests on,
// from its stored record only — what Neo read, grouped by source; the
// assumptions marked You said / Measured / Assumed; each calculation in words;
// what the answer can't tell; what is still unknown. Never a newer live value:
// the record is the snapshot the answer was written from.
//
// Pure, so cloud/web's Node vitest covers it (no DOM harness).

import {
  categoryFor,
  formatDisplay,
  formatDurationSeconds,
  isSecondsUnit,
  neoPageIsEngineHours,
  type NeoAnswerRecord,
  type NeoAssumptionOrigin,
  type NeoEvidenceRef,
  type NeoFreshness,
  type UnitSettings,
} from '@neomaris/protocol';

import { neoShortRange, neoShortTime } from './neoAnswers';

export interface NeoBehindSourceItem {
  /** e1 … e24: the anchor a "Show the evidence" chip opens. */
  id: string;
  label: string;
  /** One reading's value in the viewer's units, when the item is one value. */
  value: string | null;
  /** "Oct 3 10:50–13:40", in the answer's zone. */
  range: string | null;
  /** "Read 14:20" or "Fetched 16:05". */
  when: string | null;
  freshness: { tone: NeoFreshness; text: string };
  /** "11 of 12 samples". */
  coverage: string | null;
  /** "Measured" or "Estimated". */
  method: string | null;
  limitations: string[];
}

export interface NeoBehindSource {
  /** "Boat readings", "Engine logbook", "Open-Meteo". */
  provider: string;
  items: NeoBehindSourceItem[];
}

export interface NeoBehindAssumption {
  id: string;
  label: string;
  value: string;
  origin: NeoAssumptionOrigin;
  /** "You said", "Measured", "Assumed". */
  originText: string;
}

export interface NeoBehindResult {
  id: string;
  label: string;
  value: string;
  /**
   * "8 people × 2 days × 53 L". In the viewer's units: rebuilt from the
   * calculation's expression when their units differ from the stored ones,
   * and null when it cannot be (no expression).
   */
  words: string | null;
}

export interface NeoBehindModel {
  sources: NeoBehindSource[];
  assumptions: NeoBehindAssumption[];
  results: NeoBehindResult[];
  /** "What this can't tell you". */
  limitations: string[];
  /** "Still unknown". */
  missing: string[];
}

/** True when the record holds anything to show: an empty record opens nothing. */
export function neoAnswerHasDetails(record: NeoAnswerRecord | null | undefined): boolean {
  if (record === null || record === undefined) return false;
  return (
    record.evidence.length > 0 ||
    record.assumptions.length > 0 ||
    record.results.length > 0 ||
    record.limitations.length > 0 ||
    record.missingInputs.length > 0
  );
}

const ORIGIN_TEXT: Record<NeoAssumptionOrigin, string> = {
  owner: 'You said',
  measured: 'Measured',
  assumed: 'Assumed',
};

const FRESHNESS_TEXT: Record<NeoFreshness, string> = {
  current: 'Current',
  stale: 'Old reading',
  unknown: 'Age not known',
};

/** A number in the viewer's units: "13.4 V", "53 L", "8". */
function valueText(
  value: number,
  unit: string | undefined,
  path: string | undefined,
  units: UnitSettings
): string {
  // An engine's hour meter reads in whole hours, as the board shows it.
  if (path !== undefined && neoPageIsEngineHours(path, unit ?? null)) {
    return `${Math.round(value / 3600).toLocaleString('en-US')} h`;
  }
  // A count (people aboard, days) has no unit: said as a plain number.
  if (unit === undefined || unit === '') {
    return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  const shown = formatDisplay(value, unit, categoryFor(path, unit), units);
  return shown.unit === '' ? shown.text : `${shown.text} ${shown.unit}`;
}

/**
 * Stored words with each duration of a minute or more in seconds ("864000 s")
 * spelled as one reads it ("10 d", TOM-742). Answers stored before the cloud
 * wrote durations that way still say seconds.
 */
export function durationsInWords(words: string): string {
  return words.replace(/(\d+(?:\.\d+)?) s\b/g, (whole, n: string) => {
    const seconds = Number(n);
    return seconds >= 59.5 ? formatDurationSeconds(seconds) : whole;
  });
}

/** True when the viewer sees `unit` as it is stored: no conversion. */
function sameUnit(
  unit: string | undefined,
  path: string | undefined,
  units: UnitSettings
): boolean {
  if (unit === undefined || unit === '') return true;
  // A duration is not converted, only spelled in days, hours and minutes.
  if (isSecondsUnit(unit)) return true;
  return formatDisplay(1, unit, categoryFor(path, unit), units).unit === unit;
}

/**
 * A calculation's words in the viewer's units. The stored words are written
 * in the stored (metric) units, so beside converted values they would
 * disagree ("8 × 2 × 53 L" next to "224 gal"). Where every number in it is
 * shown as stored, the stored words stand; otherwise the expression is said
 * again with each input's converted value ("(a1 + a2) × a3" → "(6 + 2) ×
 * 14 gal"), and without an expression there are no words.
 */
function calculationWords(
  result: NeoAnswerRecord['results'][number],
  record: NeoAnswerRecord,
  units: UnitSettings,
  pathOf: (id: string | undefined) => string | undefined
): string | null {
  type Input = { value: number; unit?: string; path?: string };
  const inputOf = (id: string): Input | null => {
    const a = record.assumptions.find((x) => x.id === id);
    if (a !== undefined) return { value: a.value, unit: a.unit, path: pathOf(a.evidenceId) };
    const r = record.results.find((x) => x.id === id);
    if (r !== undefined) return { value: r.value, unit: r.unit };
    const e = record.evidence.find((x) => x.id === id);
    if (e !== undefined && typeof e.value === 'number') {
      return { value: e.value, unit: e.unit, path: e.identity?.path };
    }
    return null;
  };
  const inputs = result.inputs.map(inputOf);
  const asStored =
    sameUnit(result.unit, undefined, units) &&
    inputs.every((i) => i === null || sameUnit(i.unit, i.path, units));
  if (asStored) return durationsInWords(result.calculation.words);
  const expression = result.calculation.expression;
  if (expression === undefined) return null;
  let missing = false;
  const words = expression.replace(/\b([ear][0-9]{1,2})\b/g, (id: string) => {
    const input = inputOf(id);
    if (input === null) {
      missing = true;
      return id;
    }
    return valueText(input.value, input.unit, input.path, units);
  });
  return missing ? null : words;
}

function coverageText(coverage: NeoEvidenceRef['coverage']): string | null {
  if (coverage === undefined) return null;
  const { actual, expected } = coverage;
  const noun = (n: number) => (n === 1 ? 'sample' : 'samples');
  return expected === undefined
    ? `${actual} ${noun(actual)}`
    : `${actual} of ${expected} ${noun(expected)}`;
}

function sourceItem(
  e: NeoEvidenceRef,
  units: UnitSettings,
  nowMs: number,
  answerZone: string | undefined
): NeoBehindSourceItem {
  // Its own range's zone, else the answer's, else the viewer's.
  const zone = e.range?.timeZone ?? answerZone;
  const read = e.observedAt !== undefined ? neoShortTime(e.observedAt, nowMs, zone) : null;
  const fetched = e.fetchedAt !== undefined ? neoShortTime(e.fetchedAt, nowMs, zone) : null;
  return {
    id: e.id,
    label: e.sourceLabel,
    value:
      e.value === undefined || e.value === null
        ? null
        : valueText(e.value, e.unit, e.identity?.path, units),
    range: e.range === undefined ? null : neoShortRange(e.range.start, e.range.end, nowMs, zone),
    when: read !== null ? `Read ${read}` : fetched !== null ? `Fetched ${fetched}` : null,
    freshness: { tone: e.freshness, text: FRESHNESS_TEXT[e.freshness] },
    coverage: coverageText(e.coverage),
    method: e.method === 'estimated' ? 'Estimated' : e.method === 'measured' ? 'Measured' : null,
    limitations: e.limitations ?? [],
  };
}

/**
 * The pane's contents for one answer's record, in the viewer's units and in
 * the answer's own zone (the viewer's where it gives none). Sources keep the
 * record's order: the main item's source first.
 */
export function neoBehindModel(
  record: NeoAnswerRecord,
  units: UnitSettings,
  nowMs: number,
  viewerZone?: string
): NeoBehindModel {
  const sources: NeoBehindSource[] = [];
  for (const e of record.evidence) {
    const item = sourceItem(e, units, nowMs, record.source?.timeZone ?? viewerZone);
    const group = sources.find((s) => s.provider === e.provider);
    if (group === undefined) sources.push({ provider: e.provider, items: [item] });
    else group.items.push(item);
  }
  const pathOf = (id: string | undefined) =>
    record.evidence.find((e) => e.id === id)?.identity?.path;
  return {
    sources,
    assumptions: record.assumptions.map((a) => ({
      id: a.id,
      label: a.label,
      value: valueText(a.value, a.unit, pathOf(a.evidenceId), units),
      origin: a.origin,
      originText: ORIGIN_TEXT[a.origin],
    })),
    results: record.results.map((r) => ({
      id: r.id,
      label: r.label,
      value: valueText(r.value, r.unit, undefined, units),
      words: calculationWords(r, record, units, pathOf),
    })),
    limitations: [...record.limitations],
    missing: [...record.missingInputs],
  };
}

/**
 * The newest answer in the chat with a record to show (TOM-724): what the
 * header's Sources opens and what an open pane follows. Its id, or null.
 */
export function neoNewestAnswerWithDetails(
  messages: readonly { id: string; role: string; answer?: NeoAnswerRecord | null }[]
): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (m.role === 'assistant' && neoAnswerHasDetails(m.answer)) return m.id;
  }
  return null;
}

/**
 * Where an open Behind the answer moves when the chat changes (TOM-724): to
 * a new answer with something to show, the moment its record arrives. Null
 * leaves the pane where it is.
 *
 * - `open`: the pane is showing. A closed pane never opens by itself.
 * - `shownId`: the answer it shows now.
 * - `seenNewestId`: the newest answer with details when last looked, so an
 *   earlier answer the owner picked stays until a newer one arrives.
 * - `newestId`: the newest answer with details now. A reply with nothing to
 *   show does not change it, so the pane stays on the last one that had.
 */
export function neoBehindFollowTarget(input: {
  open: boolean;
  shownId: string | null;
  seenNewestId: string | null;
  newestId: string | null;
}): string | null {
  const { open, shownId, seenNewestId, newestId } = input;
  if (!open || newestId === null) return null;
  if (newestId === seenNewestId || newestId === shownId) return null;
  return newestId;
}

/**
 * Where a Behind the answer that is open by default goes (TOM-738): the chat
 * that slides in over a desktop page shows the newest answer's record beside
 * the thread, without being asked, until the owner closes it. Each slide-in
 * starts that afresh. Null leaves the pane as it is.
 *
 * - `byDefault`: this chat opens the pane by default (the slide-in, not the
 *   Chats page).
 * - `dismissed`: the owner closed the pane since the chat last slid in.
 * - `open`: the pane, or the board column in its slot, is showing already;
 *   moving an open pane is neoBehindFollowTarget's job.
 * - `newestId`: the newest answer with something to show.
 */
export function neoBehindDefaultTarget(input: {
  byDefault: boolean;
  dismissed: boolean;
  open: boolean;
  newestId: string | null;
}): string | null {
  const { byDefault, dismissed, open, newestId } = input;
  if (!byDefault || dismissed || open) return null;
  return newestId;
}

/**
 * Where Behind the answer goes as the chat slides in (TOM-741, as the iPad's
 * TOM-734 opens it afresh each time): the newest answer with something to
 * show, wherever the pane was left. An older answer the owner picked before
 * the chat slid out is not kept, so the chat always opens on what Neo said
 * last. Null leaves the pane as it is.
 *
 * - `byDefault`: this chat opens the pane by default (the slide-in).
 * - `boardShown`: a board an answer made is in the pane's slot; it stays.
 * - `shownId`: the answer the pane shows now, or null when it is closed.
 * - `newestId`: the newest answer with something to show.
 */
export function neoBehindSlideInTarget(input: {
  byDefault: boolean;
  boardShown: boolean;
  shownId: string | null;
  newestId: string | null;
}): string | null {
  const { byDefault, boardShown, shownId, newestId } = input;
  if (!byDefault || boardShown || newestId === null || shownId === newestId) return null;
  return newestId;
}
